// pages/PaymentSuccess.jsx

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  Container,
  Flex,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';
import { useDispatch } from 'react-redux';
import { updateInvoice } from '../../../redux/invoiceSlice';
import axios from 'axios';
import useToast from '../../../hooks/useToast';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [processed, setProcessed] = useState(false);
  const { showErrorToast, showSuccessToast } = useToast();

  useEffect(() => {
    // Prevent multiple executions
    if (processed) return;

    let isMounted = true;

    const processPayment = async () => {
      // Mark as processing to prevent multiple calls
      setProcessed(true);

      // Get parameters from URL
      const paymentId = searchParams.get('paymentId');
      const PayerID = searchParams.get('PayerID');
      const Price = searchParams.get('price');
      const invoiceId = searchParams.get('invoiceId');

      console.log('Processing payment with params:', { paymentId, PayerID, Price, invoiceId });

      if (!paymentId || !PayerID || !Price || !invoiceId) {
        if (isMounted) {
          setPaymentStatus('error');
          setLoading(false);
          showErrorToast('Missing payment parameters');
        }
        return;
      }

      try {
        // Call your backend success endpoint
        const params = new URLSearchParams({
          paymentId,
          PayerID,
          Price,
          invoiceId,
        });

        console.log('Calling PayPal success API...');
        const { data } = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/v1/payments/paypal/success?${params}`,
        );

        if (!isMounted) return; // Component unmounted, don't proceed

        console.log('PayPal API response:', data);

        if (data.success) {
          setPaymentStatus('success');
          setPaymentData({
            paymentId: data.paymentId,
            amount: data.amount,
            invoiceId: invoiceId,
            payerID: PayerID,
          });

          console.log('Updating invoice status...');
          // Update invoice status in Redux store
          try {
            const response = await dispatch(
              updateInvoice({ id: invoiceId, payload: { status: 'paid' } }),
            ).unwrap();
            console.log('Invoice update successful:', response);

            if (isMounted) {
              showSuccessToast('Payment completed successfully!');
              setLoading(false);
            }
          } catch (updateError) {
            console.error('Error updating invoice:', updateError);
            if (isMounted) {
              showErrorToast(
                updateError?.message || 'Payment processed but failed to update invoice status',
              );
              setLoading(false);
            }
          }
        } else {
          if (isMounted) {
            setPaymentStatus('error');
            showErrorToast('Payment processing failed');
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Payment processing error:', error);
        if (isMounted) {
          setPaymentStatus('error');
          showErrorToast(error?.response?.data?.error || 'Payment processing failed');
          setLoading(false);
        }
      }
    };

    processPayment();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []); // Remove dependencies to prevent re-runs

  if (loading) {
    return (
      <Container maxW="md" centerContent py={20}>
        <VStack spacing={6}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Heading size="lg" color="gray.700">
            Processing your payment...
          </Heading>
          <Text color="gray.600" textAlign="center">
            Please wait while we confirm your PayPal payment and update your invoice
          </Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Box
      maxWidth="800px"
      width="100%"
      mx="auto"
      mt="10"
      p={8}
      boxShadow="0 10px 30px rgba(0, 0, 0, 0.1)"
      rounded="xl"
      bg="white"
      borderTop={paymentStatus === 'success' ? '6px solid #38A169' : '6px solid #E53E3E'}
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '200px',
        background:
          paymentStatus === 'success'
            ? 'linear-gradient(180deg, rgba(56, 161, 105, 0.03) 0%, rgba(255, 255, 255, 0) 100%)'
            : 'linear-gradient(180deg, rgba(229, 62, 62, 0.03) 0%, rgba(255, 255, 255, 0) 100%)',
        zIndex: 0,
      }}
    >
      {paymentStatus === 'success' ? (
        <Flex direction="column" align="center" position="relative" zIndex="1">
          <Flex
            justify="center"
            align="center"
            bg="green.50"
            p={5}
            rounded="full"
            mb={8}
            width="100px"
            height="100px"
            boxShadow="0 4px 12px rgba(56, 161, 105, 0.15)"
          >
            <Icon as={CheckCircleIcon} w={12} h={12} color="green.500" />
          </Flex>

          <Heading mb={4} color="gray.800" fontSize="4xl" fontWeight="bold" letterSpacing="-0.02em">
            Payment Successful
          </Heading>

          <Text
            fontSize="xl"
            mb={8}
            color="gray.600"
            textAlign="center"
            maxW="lg"
            lineHeight="tall"
          >
            Thank you for your PayPal payment. Your invoice has been successfully processed and
            recorded.
          </Text>

          <Box
            width="100%"
            maxW="md"
            bg="gray.50"
            p={6}
            rounded="lg"
            border="1px solid"
            borderColor="gray.100"
          >
            <Flex justify="space-between" align="center" mb={5}>
              <Text fontWeight="medium" color="gray.700">
                Transaction Details
              </Text>
              <Text
                color="green.500"
                fontWeight="bold"
                fontSize="sm"
                bg="green.50"
                px={3}
                py={1}
                rounded="full"
              >
                PAID
              </Text>
            </Flex>

            <Divider mb={5} />

            <Flex direction="column" gap={4}>
              <Flex justify="space-between" align="center">
                <Text color="gray.500">Invoice ID</Text>
                <Text color="gray.800" fontWeight="medium">
                  {paymentData?.invoiceId || 'N/A'}
                </Text>
              </Flex>

              <Flex justify="space-between" align="center">
                <Text color="gray.500">Payment ID</Text>
                <Text color="gray.800" fontWeight="medium">
                  {paymentData?.paymentId || 'N/A'}
                </Text>
              </Flex>

              <Flex justify="space-between" align="center">
                <Text color="gray.500">Amount Paid</Text>
                <Text color="gray.800" fontWeight="medium">
                  ${paymentData?.amount || 'N/A'}
                </Text>
              </Flex>

              <Flex justify="space-between" align="center">
                <Text color="gray.500">Payment Date</Text>
                <Text color="gray.800" fontWeight="medium">
                  {new Date().toLocaleDateString()}
                </Text>
              </Flex>

              <Flex justify="space-between" align="center">
                <Text color="gray.500">Payment Method</Text>
                <Text color="gray.800" fontWeight="medium">
                  PayPal
                </Text>
              </Flex>
            </Flex>
          </Box>
        </Flex>
      ) : (
        <Flex direction="column" align="center" position="relative" zIndex="1">
          <Flex
            justify="center"
            align="center"
            bg="red.50"
            p={5}
            rounded="full"
            mb={8}
            width="100px"
            height="100px"
            boxShadow="0 4px 12px rgba(229, 62, 62, 0.15)"
          >
            <Icon as={WarningIcon} w={12} h={12} color="red.500" />
          </Flex>

          <Heading mb={4} color="gray.800" fontSize="4xl" fontWeight="bold" letterSpacing="-0.02em">
            Payment Failed
          </Heading>

          <Text
            fontSize="xl"
            mb={8}
            color="gray.600"
            textAlign="center"
            maxW="lg"
            lineHeight="tall"
          >
            There was an issue processing your PayPal payment. Please try again or contact support
            if the issue persists.
          </Text>

          <Box
            width="100%"
            maxW="md"
            bg="gray.50"
            p={6}
            rounded="lg"
            border="1px solid"
            borderColor="gray.100"
            mb={8}
          >
            <Flex justify="space-between" align="center" mb={5}>
              <Text fontWeight="medium" color="gray.700">
                Transaction Details
              </Text>
              <Text
                color="red.500"
                fontWeight="bold"
                fontSize="sm"
                bg="red.50"
                px={3}
                py={1}
                rounded="full"
              >
                FAILED
              </Text>
            </Flex>

            <Divider mb={5} />

            <Flex direction="column" gap={4}>
              <Flex justify="space-between" align="center">
                <Text color="gray.500">Invoice ID</Text>
                <Text color="gray.800" fontWeight="medium">
                  {searchParams.get('invoiceId') || 'N/A'}
                </Text>
              </Flex>

              <Flex justify="space-between" align="center">
                <Text color="gray.500">Attempt Date</Text>
                <Text color="gray.800" fontWeight="medium">
                  {new Date().toLocaleDateString()}
                </Text>
              </Flex>

              <Flex justify="space-between" align="center">
                <Text color="gray.500">Payment Method</Text>
                <Text color="gray.800" fontWeight="medium">
                  PayPal
                </Text>
              </Flex>
            </Flex>
          </Box>

          <VStack spacing={3} w="100%" maxW="md">
            <Button colorScheme="blue" size="lg" onClick={() => window.history.back()} w="100%">
              Try Again
            </Button>
          </VStack>
        </Flex>
      )}
    </Box>
  );
};

export default PaymentSuccess;
